import { getBase58Encoder, getBase64Encoder } from '@solana/kit';
import type { Hex } from 'viem';
import { z } from 'zod';

import { ethereumAddressSchema } from '@/lib/validation/schemas';

import { isLimitOrderSupportedChainId } from '@/constants/advancedOrder';
import { MAX_SWAP_FEE_BPS, referrers } from '@/constants/referrers';

import {
  CHAIN_IDS,
  SUPPORTED_CHAIN_IDS_LIMIT_ORDERS,
  SUPPORTED_CHAIN_IDS,
} from '../constants/chain';
import { definitiveSvmInstructionSchema } from './definitive/schemas';
import { ChainAddress, isChainAddress, isSolanaAddress, SolanaAddress } from './models';
import type { EIP712TypedData } from './signature';

// Bound raw query strings before CSV allocation and per-entry validation.
const MAX_CSV_LENGTH = 64 * 1024;
const MAX_CSV_ENTRIES = 1000;
const csvStringSchema = z.string().max(MAX_CSV_LENGTH);

/** Helper to split comma separate values */
const splitCsv = (s: string) =>
  s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);

export const chainAddressSchema = z
  .string()
  .refine((address) => isChainAddress(address), { message: 'Invalid chain address' })
  .transform((address) => address as ChainAddress);

export const solanaAddressSchema = z.custom<SolanaAddress>(
  (address) => typeof address === 'string' && isSolanaAddress(address),
  {
    message: 'Invalid Solana address',
  },
) as z.ZodType<SolanaAddress>;

/** Required comma separated wallet addresses */
export const createWalletsSchema = (maxEntries: number) =>
  z
    .string({
      error: (issue) =>
        issue.input === undefined
          ? 'wallets is required'
          : 'wallets must be a comma-separated string',
    })
    .max(MAX_CSV_LENGTH)
    .min(1, 'wallets must not be empty')
    .transform(splitCsv)
    .pipe(
      z
        .array(chainAddressSchema)
        .min(1, 'At least one valid wallet address is required')
        .max(maxEntries),
    );

export const zWalletsSchema = createWalletsSchema(MAX_CSV_ENTRIES);

export const chainIdSchema = z.coerce
  .number<string | number>()
  .refine((val: number) => SUPPORTED_CHAIN_IDS.includes(val), {
    message: 'value must be a supported chain',
  });

/** Optional comma separate chain ID's that default to all supported chains */
export const zChainIdsSchema = csvStringSchema
  .optional()
  .transform((v): (string | number)[] => (v === undefined ? SUPPORTED_CHAIN_IDS : splitCsv(v)))
  .pipe(z.array(chainIdSchema).max(MAX_CSV_ENTRIES));

/** Optional comma separated token IDs in format `address:chainId` */
export const zTokensSchema = csvStringSchema
  .optional()
  .transform((v) => {
    if (!v) return undefined;
    return splitCsv(v);
  })
  .pipe(z.array(z.string()).max(MAX_CSV_ENTRIES).optional())
  .transform((tokens) =>
    tokens?.map((token) => {
      const [address, chainId] = token.split(':');
      return { address, networkId: Number(chainId) };
    }),
  );

export const numericValueAsStringSchema = z
  .string()
  .refine((amount) => !Number.isNaN(Number(amount)), {
    message: 'value must be representable as a Number',
  });

/** Slippage in basis points: a non-negative integer string in [0, 10000]. */
export const slippageBpsSchema = z
  .string()
  .refine((value) => /^\d+$/.test(value) && Number(value) <= 10_000, {
    message: 'slippageBps must be an integer between 0 and 10000',
  });

const referrerAddresses = new Set(
  Object.values(referrers).map(({ address }) => address.toLowerCase()),
);

/**
 * Swap (integrator) fee params accepted by the swap/gasless price and quote
 * routes and forwarded verbatim to the 0x API.
 */
export const swapFeeParamsSchema = z.object({
  swapFeeBps: z.coerce.number().int().min(0).max(MAX_SWAP_FEE_BPS).optional(),
  swapFeeRecipient: chainAddressSchema
    .refine((address) => referrerAddresses.has(address.toLowerCase()), {
      message: 'Unknown swap fee recipient',
    })
    .optional(),
  swapFeeToken: chainAddressSchema.optional(),
});

export const getSourcesSchema = z.object({
  chainId: chainIdSchema,
});

export const getLimitOrdersSchema = z
  .object({
    chainId: z.coerce
      .number()
      .refine((val) => SUPPORTED_CHAIN_IDS_LIMIT_ORDERS.includes(val), {
        message: 'value must be a supported chain',
      })
      .optional(),
    searchTerm: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(1000).optional(),
    page: z.coerce.number().optional(),
    maker: chainAddressSchema.optional(),
    makers: csvStringSchema
      .transform(splitCsv)
      .pipe(z.array(chainAddressSchema).min(1).max(2))
      .optional(),
    makerToken: chainAddressSchema.optional(),
    takerToken: chainAddressSchema.optional(),
    /**
     * Scope filter: comma separated `address:chainId` entries. An order matches
     * when it involves any listed token on its chain, or all of them when
     * `pairOnly` is set.
     */
    tokens: csvStringSchema
      .transform(splitCsv)
      .pipe(
        z
          .array(
            z.string().refine(
              (token) => {
                const parts = token.split(':');
                const chainId = Number(parts[1]);
                return (
                  parts.length === 2 &&
                  isChainAddress(parts[0]) &&
                  Number.isSafeInteger(chainId) &&
                  chainId > 0
                );
              },
              { message: 'tokens entries must be `address:chainId`' },
            ),
          )
          .min(1)
          .max(2),
      )
      .transform((entries) =>
        entries.map((token) => {
          const [address, chainId] = token.split(':');
          return { address: address as ChainAddress, chainId: Number(chainId) };
        }),
      )
      .optional(),
    pairOnly: z
      .enum(['true', 'false'])
      .optional()
      .transform((value) => value === 'true'),
  })
  .superRefine(({ maker, makers }, ctx) => {
    if ((maker === undefined) === (makers === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Exactly one of maker or makers is required',
        path: ['maker'],
      });
    }
  });

export const limitOrderSchema = z.object({
  chainId: z.coerce.number(),
  expiry: z.coerce.bigint(),
  feeRecipient: ethereumAddressSchema,
  maker: ethereumAddressSchema,
  makerAmount: z.coerce.bigint(),
  makerToken: ethereumAddressSchema,
  pool: z.string(),
  salt: z.coerce.bigint(),
  sender: ethereumAddressSchema,
  taker: ethereumAddressSchema,
  takerAmount: z.coerce.bigint(),
  takerToken: ethereumAddressSchema,
  takerTokenFeeAmount: z.coerce.bigint(),
  verifyingContract: ethereumAddressSchema,
});

export const oneInchLimitOrderSchema = z.object({
  orderHash: z.string(),
  signature: z.string(),
  rawOrder: z.object({
    /*
    salt: string;
    maker: string;
    receiver: string;
    makerAsset: string;
    takerAsset: string;
    makingAmount: string;
    takingAmount: string;
    makerTraits: string;
    */
    salt: z.string(),
    maker: z.string(),
    receiver: z.string(),
    makerAsset: z.string(),
    takerAsset: z.string(),
    makingAmount: z.string(),
    takingAmount: z.string(),
    makerTraits: z.string(),
    extension: z.string(),
  }),
});

export type OneInchLimitRawOrder = z.infer<typeof oneInchLimitOrderSchema>['rawOrder'];

const limitOrderTypedDataSchema = z.object({
  domain: z.object({
    name: z.string().optional(),
    version: z.string().optional(),
    chainId: z.number().int().optional(),
    verifyingContract: ethereumAddressSchema.optional(),
    salt: z
      .string()
      .regex(/^0x[0-9a-fA-F]*$/)
      .transform((salt): Hex => salt as Hex)
      .optional(),
  }),
  types: z
    .record(
      z.string(),
      z.array(
        z.object({
          name: z.string(),
          type: z.string(),
        }),
      ),
    )
    .transform((types): EIP712TypedData['types'] => types as unknown as EIP712TypedData['types']),
  primaryType: z.string(),
  message: z.record(z.string(), z.unknown()),
});

export const oneInchLimitOrderBuildResponseSchema = z.object({
  provider: z.literal('1inch'),
  chainFamily: z.literal('evm'),
  typedData: limitOrderTypedDataSchema,
  orderHash: z
    .string()
    .regex(/^0x[0-9a-fA-F]+$/)
    .transform((orderHash): Hex => orderHash as Hex),
  rawOrder: oneInchLimitOrderSchema.shape.rawOrder,
});

const definitiveLimitOrderEstimateFields = {
  netBuyAmount: z.string().regex(/^\d+$/),
  estimatedFeesUsd: z.string().min(1),
} as const;

const definitiveSignableFields = {
  ...definitiveLimitOrderEstimateFields,
  provider: z.literal('definitive'),
  signable: z.literal(true),
  quoteId: z.string().min(1),
  expiresAt: z.number().int().positive(),
} as const;

export const definitiveLimitOrderPreviewResponseSchema = z.object({
  ...definitiveLimitOrderEstimateFields,
  provider: z.literal('definitive'),
  chainFamily: z.enum(['evm', 'svm']),
  signable: z.literal(false),
});

export const definitiveEvmLimitOrderBuildResponseSchema = z.object({
  ...definitiveSignableFields,
  chainFamily: z.literal('evm'),
  approveTx: z
    .object({
      to: ethereumAddressSchema,
      data: z
        .string()
        .regex(/^0x[0-9a-fA-F]*$/)
        .transform((data): Hex => data as Hex),
    })
    .nullable(),
  permitTypedData: z.string().min(1).nullable(),
  orderTypedData: z.string().min(1),
});

export const definitiveSvmLimitOrderBuildResponseSchema = z.object({
  ...definitiveSignableFields,
  chainFamily: z.literal('svm'),
  ataSetupIxs: z.array(definitiveSvmInstructionSchema).nullable(),
  delegateIx: definitiveSvmInstructionSchema.nullable(),
  sponsoredDelegateTx: z.string().min(1).nullable(),
  wrapInstructions: z.array(definitiveSvmInstructionSchema).nullable(),
  orderMessage: z.string().min(1),
  nonce: z.string().regex(/^\d+$/),
  deadline: z.string().regex(/^\d+$/),
});

export const limitOrderBuildResponseSchema = z.union([
  oneInchLimitOrderBuildResponseSchema,
  definitiveEvmLimitOrderBuildResponseSchema,
  definitiveSvmLimitOrderBuildResponseSchema,
  definitiveLimitOrderPreviewResponseSchema,
]);

export const oneInchLimitOrderSubmissionSchema = oneInchLimitOrderSchema.extend({
  provider: z.literal('1inch'),
  chainId: z.number().int().refine(isLimitOrderSupportedChainId, {
    message: 'value must be a supported chain',
  }),
  baseAmount: z.coerce.bigint(),
});

export const definitiveEvmLimitOrderSubmissionSchema = z
  .object({
    provider: z.literal('definitive'),
    chainId: z
      .number()
      .int()
      .refine(isLimitOrderSupportedChainId, {
        message: 'value must be a supported chain',
      })
      .refine((chainId) => chainId !== CHAIN_IDS.SOLANA, 'Expected an EVM chain'),
    quoteId: z.string().min(1),
    userSignature: z.string().min(1),
    evmPermitSignature: z.string().min(1).optional(),
  })
  .strict();

export const definitiveSvmLimitOrderSubmissionSchema = z
  .object({
    provider: z.literal('definitive'),
    chainId: z.literal(CHAIN_IDS.SOLANA),
    quoteId: z.string().min(1),
    userSignature: z.string().refine((signature) => {
      try {
        return getBase58Encoder().encode(signature).length === 64;
      } catch {
        return false;
      }
    }, 'Expected a 64-byte base58 Ed25519 signature'),
    svmNonce: z.string().regex(/^\d+$/),
    svmDeadline: z.string().regex(/^\d+$/),
    svmSponsoredDelegateTx: z
      .string()
      .refine((transaction) => {
        try {
          return getBase64Encoder().encode(transaction).length > 0;
        } catch {
          return false;
        }
      }, 'Invalid sponsored delegation transaction')
      .optional(),
  })
  .strict();

export const definitiveLimitOrderSubmissionSchema = z.union([
  definitiveEvmLimitOrderSubmissionSchema,
  definitiveSvmLimitOrderSubmissionSchema,
]);

export const limitOrderSubmissionSchema = z.union([
  oneInchLimitOrderSubmissionSchema,
  definitiveEvmLimitOrderSubmissionSchema,
  definitiveSvmLimitOrderSubmissionSchema,
]);

// https://portal.1inch.dev/documentation/apis/orderbook/swagger?method=get&path=/v4.0/1/address/%7Baddress%7D
export const oneInchLimitOrdersResponseItem = z.object({
  signature: z.string().optional(),
  orderHash: z.string(),
  createDateTime: z.string(),
  remainingMakerAmount: z.string(),
  makerBalance: z.string(),
  makerAllowance: z.string(),
  orderStatus: z.number().optional(),
  data: z.object({
    makerAsset: z.string(),
    takerAsset: z.string(),
    maker: z.string(),
    makingAmount: z.string(),
    takingAmount: z.string(),
    salt: z.string(),
    extension: z.string(),
    makerTraits: z.string(),
  }),
  makerRate: z.string(),
  takerRate: z.string(),
  isMakerContract: z.boolean(),
  orderInvalidReason: z.string().nullable(),
  events: z.array(z.object({ action: z.string(), amount: z.string().optional() })).optional(),
});

export const oneInchLimitOrdersResponseSchema = z.object({
  meta: z
    .object({
      hasMore: z.boolean(),
      nextCursor: z.string().optional(),
      count: z.number().optional(),
    })
    .optional(),
  items: oneInchLimitOrdersResponseItem.array(),
});
